import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AdditionalAssetMeta } from "@amzn/afxtaco-types";
import { reducerType } from "../../redux/reducers";
import {
  clearAdditionalAssets,
  setAdditionalAssetList,
} from "../../redux/reducers/AdditionalAssets";
import { listAdditionalAssets } from "../api/assets/assets";

interface UseAdditionalAssetsOptions {
  projectId: string | undefined;
  /** Skip fetching (e.g. during the new-project flow before assets exist). */
  skip?: boolean;
}

interface UseAdditionalAssetsResult {
  /** The project's additional assets (empty until first load completes). */
  assets: AdditionalAssetMeta[];
  /** Whether the list is currently being fetched. */
  isLoading: boolean;
}

/**
 * Fetches and caches the list of "additional" assets (arbitrary, non-canonical
 * files like `research/notes.md`) for a project.
 *
 * Re-fetches when the project's `assetsStaleCounter` bumps — the completion
 * thunk bumps it on any asset-generation turn, so newly-written additional
 * assets show up after the agent finishes. The list is stored in the dedicated
 * `AdditionalAssets` slice (path-keyed), separate from the canonical asset cache.
 */
export const useAdditionalAssets = ({
  projectId,
  skip = false,
}: UseAdditionalAssetsOptions): UseAdditionalAssetsResult => {
  const dispatch = useDispatch();

  const assets = useSelector((state: reducerType) =>
    projectId ? state.AdditionalAssets.byProject[projectId]?.list : undefined,
  );

  // The asset-generation signal — same counter useProjectAsset watches.
  const staleCounter = useSelector((state: reducerType) =>
    projectId ? (state.Projects.assetsStaleCounter[projectId] ?? 0) : 0,
  );

  const [isLoading, setIsLoading] = useState(false);

  // Track which project we've already fetched so we don't refetch every render.
  const attemptedRef = useRef<string | undefined>(undefined);

  const doFetch = useCallback(async () => {
    if (!projectId || skip) return;
    attemptedRef.current = projectId;
    setIsLoading(true);
    try {
      const response = await listAdditionalAssets(projectId);
      if (response?.success) {
        dispatch(
          setAdditionalAssetList({
            projectId,
            assets: response.data.assets,
          }),
        );
      }
    } catch (error) {
      console.error("Failed to list additional assets:", error);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, skip, dispatch]);

  // Auto-fetch on mount / when the project becomes available.
  useEffect(() => {
    if (projectId && !skip && attemptedRef.current !== projectId) {
      doFetch();
    }
  }, [projectId, skip, doFetch]);

  // On a new asset-generation turn, drop cached content and re-fetch the list so
  // newly-generated additional assets appear.
  const prevStaleCounterRef = useRef(staleCounter);
  useEffect(() => {
    if (staleCounter > prevStaleCounterRef.current && projectId && !skip) {
      dispatch(clearAdditionalAssets({ projectId }));
      attemptedRef.current = undefined;
      doFetch();
    }
    prevStaleCounterRef.current = staleCounter;
  }, [staleCounter, projectId, skip, dispatch, doFetch]);

  return { assets: assets ?? [], isLoading };
};
